import DefaultImage from "@assets/tm_article_default.svg";
import ChevronImage from "@assets/video_widget/Chevron.webp";
import Image from "@elements/ResponsiveImage/Image";
import Date from "@elements/Typography/Date";
import Heading from "@elements/Typography/Heading";
import { ProcessedArticleData } from "@helper/transformer/useOsResponse";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import { useEffect, useRef } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";

type TrendingArticleCarouselProps = Readonly<{
  testId?: string;
  rootClassName?: string;
  dataSectionName?: string;
  trendingArticles: ProcessedArticleData[];
  source?: string;
  loadGaEvent?: boolean;
}>;

type WithGaEventLoad = {
  loadGaEvent: true;
  pageUrl: string;
};

type WithoutGaEventLoad = {
  loadGaEvent?: false | undefined;
  pageUrl?: string;
};

export enum CAROUSEL_CONTROL_DIRECTION {
  RIGHT = "right",
  LEFT = "left",
}

export type CarousalProps = TrendingArticleCarouselProps &
  (WithGaEventLoad | WithoutGaEventLoad);

export default function TrendingArticleCarousel({
  testId,
  rootClassName,
  dataSectionName,
  trendingArticles,
  source,
  loadGaEvent = false,
  pageUrl,
}: CarousalProps): React.ReactElement {
  const TmCustomGaEvent = useTMCustomEventAnalyticsCall();
  const hasRun = useRef(false);
  useEffect(() => {
    if (loadGaEvent && !hasRun.current) {
      TmCustomGaEvent({
        event: "customEvent",
        eventCategory: "trending-story-middle",
        eventAction: "Visible",
        eventLabel: `${origin}${pageUrl}`,
      });
      hasRun.current = true;
    }
  }, [TmCustomGaEvent, loadGaEvent, pageUrl]);

  const sliderRef = useRef<HTMLDivElement>(null);

  return (
    <div
      className={twMerge("px-0 py-5 mb-5", rootClassName)}
      data-testid={testId}
      data-section-name={dataSectionName}
    >
      <div className="relative flex">
        <Heading
          Element="h2"
          className="font-primary font-bold text-sm mb-1 text-red-100"
        >
          <Link
            reloadDocument
            to={`/quick-news?ref=${source || "trending-story-middle"}`}
            target="_blank"
          >
            இவையும் உங்களுக்குப் பிடிக்கலாம்
            <img
              alt="toggle-button-chevie"
              height={26}
              width={26}
              className="pl-100 inline"
              src={ChevronImage}
            />
          </Link>
        </Heading>
      </div>
      <div className="text-xs mb-5">அனைத்துவகைச் செய்திகளும் ஒரே இடத்தில்.</div>
      <div className="relative">
        <div
          ref={sliderRef}
          className="flex overflow-y-hidden gap-2.5 items-start justify-start hide-scrollbar overflow-x-scroll [-webkit-overflow-scrolling:touch] lg:overflow-x-hidden"
        >
          {trendingArticles.map((item, index) => {
            return (
              <div
                className="relative min-w-[190px] border-0 rounded-[15px] overflow-hidden"
                key={item.id}
              >
                <Link
                  reloadDocument
                  to={`${item.linkToQuick}?ref=${source || "trending-story-middle"}&pos=${index + 1}`}
                  target="_blank"
                >
                  <div className="relative after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-[166px] after:bg-gradient-to-t after:from-black-100 after:to-transparent">
                    <Image
                      displayWidth={480}
                      srcWidth={480}
                      srcHeight={320}
                      src={item.imageField?.url ?? DefaultImage}
                      alt={item.imageAlt ?? item.title}
                      className={twMerge("w-[190px] object-cover h-[275px]")}
                    />
                  </div>
                  <div className="absolute bottom-2 w-[190px] p-2.5">
                    <Heading
                      Element="h3"
                      className={twMerge(
                        "text-red-500 text-2sm font-semibold leading-[150%] font-primary ",
                        "overflow-hidden overflow-ellipsis",
                      )}
                    >
                      {item.title}
                    </Heading>

                    <Date className="text-gray-2050 text-xs leading-[150%]">
                      {item.fullDate}
                      {item.time && (
                        <span className="lowercase"> - {item.time}</span>
                      )}
                    </Date>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
